import React, { FC, useState } from 'react';
import { Field, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import {
	Box,
	Button,
	makeStyles,
	TextField,
	Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

type Form = {
	name: string;
	email: string;
	question: string;
};

const initialValues: Form = {
	name: '',
	email: '',
	question: '',
};

const validationSchema = Yup.object().shape({
	name: Yup.string().nullable().required('Povinné pole'),
	email: Yup.string().nullable().required('Povinné pole'),
	question: Yup.string().nullable().required('Povinné pole'),
});

const useStyles = makeStyles(theme => ({
	spaceTopBottom: {
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
	},
}));

const ContactForm: FC = () => {
	const classes = useStyles();

	const { t } = useTranslation('contact_form');

	const [pending, setPending] = useState(false);

	const [submitted, setSubmitted] = useState(false);

	const handleSubmit = (values: Form) => {
		setPending(true);

		try {
			const bodyFormData = new FormData();

			bodyFormData.append('name', values.name);
			bodyFormData.append('email', values.email);
			bodyFormData.append('text', values.question);

			axios
				.post(`${process.env.PUBLIC_URL}/api/scitacioperaty/contact`, {
					name: values.name,
					email: values.email,
					text: values.question,
				})
				.then(() => {
					setPending(false);
					setSubmitted(true);
				});
		} catch (e) {
			console.error(e);

			throw e;
		}
	};

	const formik = useFormik({
		initialValues,
		onSubmit: handleSubmit,
		validationSchema,
	});

	return (
		<form onSubmit={formik.handleSubmit}>
			<FormikProvider value={formik}>
				<Field
					id="name"
					name="name"
					component={TextField}
					className={classes.spaceTopBottom}
					label={t('fields.name')}
					variant="outlined"
					fullWidth
					disabled={pending}
					onChange={formik.handleChange}
					value={formik.values.name}
					error={Boolean(formik.errors.name)}
					helperText={formik.errors.name}
				/>
				<Field
					id="email"
					name="email"
					component={TextField}
					className={classes.spaceTopBottom}
					label={t('fields.email')}
					variant="outlined"
					fullWidth
					disabled={pending}
					onChange={formik.handleChange}
					value={formik.values.email}
					error={Boolean(formik.errors.email)}
					helperText={formik.errors.email}
				/>
				<Field
					id="question"
					name="question"
					component={TextField}
					className={classes.spaceTopBottom}
					label={t('fields.question')}
					variant="outlined"
					fullWidth
					disabled={pending}
					multiline
					rows={6}
					onChange={formik.handleChange}
					value={formik.values.question}
					error={Boolean(formik.errors.question)}
					helperText={formik.errors.question}
				/>
				<Box
					className={classes.spaceTopBottom}
					display="flex"
					flexDirection="column"
					alignItems="flex-end"
				>
					<Button
						type="submit"
						disabled={pending}
						variant="contained"
						color="primary"
					>
						{t('submit_button')}
					</Button>
					{submitted && (
						<Typography variant="h6" color="textSecondary" paragraph>
							{t('question_submitted')}
						</Typography>
					)}
				</Box>
			</FormikProvider>
		</form>
	);
};

export default ContactForm;
